import { objectMap, asyncTimeout } from '@caresend/utils';
import cloneDeep from 'lodash.clonedeep';
import { defineComponent, ref, computed, onMounted, watch } from 'vue';
import __vue_component__ from '../../buttons/ButtonComponent/ButtonComponent.vue.js';
import { ButtonStyle } from '../../buttons/ButtonComponent/model.js';
import __vue_component__$2 from '../../cards/BasicCard/BasicCard.vue.js';
import { CardStyle } from '../../cards/BasicCard/model.js';
import { getObjectValueFromPath, isDCForm, isDCLink, isDCTitleSubtitle, replaceSymbols, isDCEmbed } from './helpers.js';
import { SupportedDynamicCopyComponent, DCComponents } from './model.js';
import __vue_component__$1 from '../../transitions/TransitionDownUp/TransitionDownUp.vue.js';
import { getStore } from '../../../store/helpers/initStore.js';

var script = defineComponent({
    name: 'DynamicCopy',
    components: {
        ButtonComponent: __vue_component__,
        TransitionDownUp: __vue_component__$1,
    },
    props: {
        dynamicCopy: {
            type: Object,
            required: true,
        },
        component: {
            type: String,
            default: undefined,
        },
        noColor: Boolean,
    },
    setup(props, { emit }) {
        const store = getStore();
        const mountError = ref(false);
        const mountComponent = ref();
        const defaultProps = ref({});
        const title = ref(undefined);
        const subtitle = ref(undefined);
        const link = ref(undefined);
        const openLink = () => {
            if (!link.value)
                return;
            window.open(link.value.url, '_blank');
        };
        /**
         * Various styles and replacements will be made depending on the
         * defined component prop and the setup of the input DynamicCopy object
         */
        const titleSubtitleDisplay = computed(() => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j;
            if (props.component === SupportedDynamicCopyComponent.ALERT) {
                return {
                    title: {
                        value: subtitle.value,
                        classes: ['DynamicCopy__title'],
                    },
                };
            }
            const display = {
                title: {
                    value: (_a = title.value) !== null && _a !== void 0 ? _a : '',
                    classes: ['DynamicCopy__title'],
                },
            };
            if (props.noColor)
                (_c = (_b = display.title) === null || _b === void 0 ? void 0 : _b.classes) === null || _c === void 0 ? void 0 : _c.push('DynamicCopy__title--no-color');
            if (subtitle.value) {
                (_e = (_d = display.title) === null || _d === void 0 ? void 0 : _d.classes) === null || _e === void 0 ? void 0 : _e.push('DynamicCopy__title--bold');
                display.subtitle = {
                    value: subtitle.value,
                    classes: ['DynamicCopy__subtitle'],
                };
                if (props.noColor) {
                    (_g = (_f = display.subtitle) === null || _f === void 0 ? void 0 : _f.classes) === null || _g === void 0 ? void 0 : _g.push('DynamicCopy__title--no-color');
                    (_j = (_h = display.title) === null || _h === void 0 ? void 0 : _h.classes) === null || _j === void 0 ? void 0 : _j.push('DynamicCopy__title--bold-no-color');
                }
            }
            return display;
        });
        /**
         * Variables to manage the state of the form and its options
         *
         * Previous forms contains the value of the selected option and the form
         * to retain the selection made by the user in previous form steps
         */
        const previousForms = ref([]);
        const currentForm = ref(undefined);
        const selectedFormOption = ref(undefined);
        /**
         * Variables to manage the symbols used for replacement
         */
        const symbols = computed(() => { var _a, _b; return (_b = (_a = store.state.variables.variables) === null || _a === void 0 ? void 0 : _a.dynamicCopy) === null || _b === void 0 ? void 0 : _b.symbols; });
        const mappedSymbols = computed(() => { var _a; return objectMap((_a = symbols.value) !== null && _a !== void 0 ? _a : {}, (path) => { var _a; return (_a = getObjectValueFromPath(store.state, path)) !== null && _a !== void 0 ? _a : 'Not found'; }); });
        const setup = () => {
            var _a;
            if (isDCTitleSubtitle(props.dynamicCopy)) {
                mountComponent.value = __vue_component__$2;
                title.value = replaceSymbols(props.dynamicCopy.title, mappedSymbols.value);
                subtitle.value = replaceSymbols(props.dynamicCopy.subtitle, mappedSymbols.value);
                defaultProps.value = {
                    cardStyle: CardStyle.WHITE_WITH_BORDER,
                };
            }
            if (isDCForm(props.dynamicCopy)) {
                mountComponent.value = 'div';
                title.value = replaceSymbols(props.dynamicCopy.title, mappedSymbols.value);
                subtitle.value = replaceSymbols(props.dynamicCopy.subtitle, mappedSymbols.value);
                currentForm.value = props.dynamicCopy;
            }
            if (isDCLink(props.dynamicCopy)) {
                subtitle.value = undefined;
                link.value = {
                    text: (_a = replaceSymbols(props.dynamicCopy.subtitle, mappedSymbols.value)) !== null && _a !== void 0 ? _a : 'Missing subtitle for link',
                    url: props.dynamicCopy.url,
                };
                defaultProps.value = {
                    cardStyle: CardStyle.GREY,
                };
            }
            if (!isDCTitleSubtitle(props.dynamicCopy)
                && !isDCForm(props.dynamicCopy)
                && !isDCLink(props.dynamicCopy)
                && isDCEmbed(props.dynamicCopy)) {
                console.error('DynamicCopy embed is not supported here.');
                mountError.value = true;
            }
            if (props.component) {
                if (Object.values(SupportedDynamicCopyComponent).includes(props.component)) {
                    mountComponent.value = DCComponents[props.component];
                    return;
                }
                console.error(`DynamicCopy component ${props.component} is not supported`);
                mountError.value = true;
            }
        };
        onMounted(setup);
        watch(() => props.dynamicCopy, setup);
        const getOptionButtonStyle = (option, index) => {
            var _a, _b, _c, _d;
            if (option.value === ((_a = selectedFormOption.value) === null || _a === void 0 ? void 0 : _a.value)
                && ((_c = (_b = currentForm.value) === null || _b === void 0 ? void 0 : _b.options[index]) === null || _c === void 0 ? void 0 : _c.value) === ((_d = selectedFormOption.value) === null || _d === void 0 ? void 0 : _d.value))
                return ButtonStyle.OUTLINE;
            return ButtonStyle.OUTLINE_WHITE;
        };
        const getOptionButtonClass = (option) => {
            var _a;
            const classNames = [];
            if (option.value === ((_a = selectedFormOption.value) === null || _a === void 0 ? void 0 : _a.value))
                classNames.push('DynamicCopy__form-option--selected');
            return classNames;
        };
        const formCanNavigate = computed(() => isDCForm(props.dynamicCopy) && props.dynamicCopy.options.some((option) => !!option.subForm));
        const nextButtonText = computed(() => {
            var _a, _b;
            if ((_a = selectedFormOption.value) === null || _a === void 0 ? void 0 : _a.subForm)
                return 'Next';
            return ((_b = currentForm.value) === null || _b === void 0 ? void 0 : _b.options.some((option) => !!option.subForm)) ? 'Next' : 'Confirm';
        });
        /**
         * Reset the current form and optionally remove the context of previous forms
         * with a timeout to allow for the transition to complete
         */
        const resetForm = async (removeContext, withTimeout) => {
            if (removeContext)
                previousForms.value = [];
            currentForm.value = undefined;
            title.value = undefined;
            subtitle.value = undefined;
            selectedFormOption.value = undefined;
            if (withTimeout)
                await asyncTimeout(300);
        };
        const navigateBack = async () => {
            var _a, _b;
            if (previousForms.value.length === 0)
                return;
            await resetForm(false, true);
            const formValuePair = previousForms.value.pop();
            currentForm.value = formValuePair === null || formValuePair === void 0 ? void 0 : formValuePair.form;
            title.value = replaceSymbols((_a = currentForm.value) === null || _a === void 0 ? void 0 : _a.title, mappedSymbols.value);
            subtitle.value = replaceSymbols((_b = currentForm.value) === null || _b === void 0 ? void 0 : _b.subtitle, mappedSymbols.value);
        };
        const navigateNext = async () => {
            const selectedOption = cloneDeep(selectedFormOption.value);
            if (!currentForm.value)
                return;
            if (!selectedOption)
                return;
            /** Keep track of the selection context */
            previousForms.value.push({
                value: selectedOption.value,
                form: currentForm.value,
            });
            /** If no subform exists, we're at the end of the rope */
            if (!selectedOption.subForm) {
                const prevSelectedValues = previousForms.value.map((formValuePair) => formValuePair.value);
                emit('confirm', prevSelectedValues);
                await asyncTimeout(500);
                await resetForm(true, false);
                if (isDCForm(props.dynamicCopy)) {
                    title.value = replaceSymbols(props.dynamicCopy.title, mappedSymbols.value);
                    subtitle.value = replaceSymbols(props.dynamicCopy.subtitle, mappedSymbols.value);
                    currentForm.value = props.dynamicCopy;
                    return;
                }
                return;
            }
            await resetForm(false, true);
            title.value = replaceSymbols(selectedOption.subForm.title, mappedSymbols.value);
            subtitle.value = replaceSymbols(selectedOption.subForm.subtitle, mappedSymbols.value);
            currentForm.value = selectedOption.subForm;
            selectedFormOption.value = undefined;
        };
        const selectOption = async (index, value) => {
            if (!currentForm.value)
                return;
            const option = currentForm.value.options[index];
            if ((option === null || option === void 0 ? void 0 : option.value) !== value)
                return;
            selectedFormOption.value = option;
        };
        return {
            SupportedDynamicCopyComponent,
            currentForm,
            defaultProps,
            formCanNavigate,
            getOptionButtonClass,
            getOptionButtonStyle,
            isDCForm,
            isDCLink,
            isDCTitleSubtitle,
            link,
            mappedSymbols,
            mountComponent,
            mountError,
            navigateBack,
            navigateNext,
            nextButtonText,
            openLink,
            previousForms,
            replaceSymbols,
            selectOption,
            selectedFormOption,
            subtitle,
            title,
            titleSubtitleDisplay,
        };
    },
});

export { script as default };
